<template>
  <atom-collapse :isHeaderPaddingLeft="false">
    <template #header>
      <div class="px-2 w-100">
        <div
          class="d-flex flex-row justify-content-between align-items-center w-100 flex-start gap-12px flex-wrap"
        >
          <div
            class="d-flex gap-8px align-items-center flex-1 min-w-250px"
          >
            <div class="icon-menu-container">
              <feather-icon
                :icon="resourceTypeIcon[data.resource.type]"
                size="22"
                :stroke="primaryColor"
              />
            </div>
            <div>
              <div class="header">
                {{ dateTime.date }}
                <span class="font-weight-medium">{{ dateTime.time }}</span>
              </div>
              <div class="sub-header">
                {{ formatTimeAgo(data.dateTime) }}
              </div>
            </div>
          </div>

          <div
            class="d-flex gap-10px align-items-center flex-1 min-w-250px"
          >
            <div>
              <b-badge
                pill
                :variant="getVariantBadge(data.action)"
                class="badge-style"
              >
                {{ data.action }}
              </b-badge>
            </div>
            <div>
              <div class="header">
                {{ resourceTypeHeader[data.resource.type] }}
                <span
                  v-if="
                    !data.isDeleted &&
                    data.resource.type !== resourceType.REPORT
                  "
                  class="header-link text-primary"
                  @click.stop="
                    onClickResourceTitle(data.resource, data.items)
                  "
                  >“{{ data.resource.title }}”
                </span>
                <span v-else>“{{ data.resource.title }}”</span>
              </div>
              <div class="sub-header">Site: {{ data.site || "-" }}</div>
            </div>
          </div>

          <div
            class="d-flex gap-10px align-items-center flex-1 min-w-250px"
          >
            <div>
              <b-avatar
                variant="light-primary"
                size="32"
                :text="getFirtLetter(data.name)"
              />
            </div>
            <div>
              <div>{{ data.name }}</div>
              <div class="sub-header">{{ data.username }}</div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <template>
      <div class="row">
        <div class="col-12 table-wrapper">
          <b-table
            sticky-header
            responsive
            :items="data.items"
            :fields="fields"
            show-empty
          >
            <template #cell(key)="data">
              <div
                class="d-flex gap-8px align-items-center min-w-300px w-100"
              >
                <div>{{ data.value }}</div>
                <div
                  v-if="data.item.isUpdated"
                  class="d-flex align-items-center"
                >
                  <feather-icon
                    icon="AlertTriangleIcon"
                    size="16"
                    :stroke="warningColor"
                  />
                </div></div
            ></template>
            <template #cell(oldValue)="data">
              <div
                v-html="formatItemChangeValueForDisplay(data.value) || '-'"
                class="min-w-300px w-100"
              ></div>
            </template>
            <template #cell(newValue)="data">
              <div
                v-html="formatItemChangeValueForDisplay(data.value) || '-'"
                class="min-w-300px w-100"
              ></div>
            </template>
          </b-table>
        </div>
      </div>
    </template>
  </atom-collapse>
</template>

<script>
import { DateTime } from "luxon"
import {
  ResourceType,
  ResourceTypeIcon,
  ResourceTypeHeader,
  ResourceRouteMap,
  ActionType
} from "@/constants/workload"
import AtomCollapse from "@/components/atoms/AtomCollapse.vue"
import { BAvatar } from "bootstrap-vue"
import { BBadge } from "bootstrap-vue"
import { BTable } from "bootstrap-vue"
import { mapActions, mapGetters } from "vuex"

export default {
  name: "MoleculeWorkloadItem",
  components: {
    AtomCollapse,
    BAvatar,
    BBadge,
    BTable
  },
  props: {
    data: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data() {
    return {
      resourceTypeIcon: ResourceTypeIcon,
      resourceTypeHeader: ResourceTypeHeader,
      resourceType: ResourceType,
      actionType: ActionType,
      primaryColor: "#7367F0",
      warningColor: "#FF9F43",
      fields: [
        {
          key: "key",
          label: "Key",
          thClass: "min-w-300px",
          tdClass: "min-w-300px"
        },
        {
          key: "oldValue",
          label: "Old Value",
          thClass: "min-w-300px",
          tdClass: "min-w-300px"
        },
        {
          key: "newValue",
          label: "New Value",
          thClass: "min-w-300px",
          tdClass: "min-w-300px"
        }
      ]
    }
  },
  computed: {
    ...mapGetters("appFilter", ["getInternalIpAddressManagerFilter"]),

    dateTime() {
      return {
        date: DateTime.fromISO(this.data.dateTime).toFormat("dd/MM/yyyy"),
        time: DateTime.fromISO(this.data.dateTime).toFormat("HH:mm")
      }
    }
  },
  methods: {
    ...mapActions("appFilter", ["setInternalIpAddressManagerFilter"]),

    formatTimeAgo(date) {
      const now = DateTime.now()
      const provided = DateTime.fromISO(date)

      const diffInMinutes = now.diff(provided, "minutes").minutes
      const diffInHours = now.diff(provided, "hours").hours
      const diffInDays = now.diff(provided, "days").days

      if (diffInDays >= 1) {
        const days = Math.floor(diffInDays)
        return days === 1 ? "1 day ago" : `${days} days ago`
      } else if (diffInHours >= 1) {
        const hours = Math.floor(diffInHours)
        return hours === 1 ? "1 hour ago" : `${hours} hours ago`
      } else if (diffInMinutes >= 1) {
        const minutes = Math.floor(diffInMinutes)
        return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`
      } else {
        return "Just now"
      }
    },
    formatItemChangeValueForDisplay(value) {
      if (value === null || value === undefined) {
        return "-"
      }

      if (typeof value === "string") {
        if (
          value.includes(",") &&
          !value.trim().startsWith("{") &&
          !value.trim().startsWith("[")
        ) {
          return value
            .split(",")
            .map((v) => v.trim())
            .join(",<br />")
        }

        try {
          const parsed = JSON.parse(value)
          if (parsed.length === 0) {
            return "-"
          }

          if (Array.isArray(parsed)) {
            const result = parsed
              .map((item) => {
                if (item.FileName) {
                  return `<a href="${item.FileUrl}" target="_blank">${item.FileName}</a>`
                }

                const stringified = JSON.stringify(item, null, 4)
                return stringified.replace(/,/g, ",<br />")
              })
              .join(", <br />")

            return result
          }
        } catch (e) {
          return value
        }
      }

      if (Array.isArray(value)) {
        return value.join(", <br />")
      }

      return value
    },
    getFirtLetter(data) {
      return data.charAt(0).toUpperCase()
    },
    getVariantBadge(type) {
      switch (type) {
        case ActionType.CREATE:
          return "light-success"
        case ActionType.UPDATE:
          return "light-warning"
        case ActionType.DELETE:
          return "light-danger"
      }
    },
    getResourceRoute(resource) {
      return ResourceRouteMap[resource] || null
    },
    onClickResourceTitle(resource, items) {
      const routeName = this.getResourceRoute(resource.type)

      switch (resource.type) {
        case ResourceType.TICKET:
          this.$router.push({
            name: routeName,
            params: { ticketId: resource.id }
          })
          break
        case ResourceType.ESCALATION:
          this.$router.push({
            name: routeName,
            params: { escalationTemplateId: resource.id }
          })
          break
        case ResourceType.IPAM:
          const subnetId = resource.title

          const ipChange = items.find((item) => item.key === "Ip")
          const ipAddress = ipChange ? ipChange.newValue : null

          this.setInternalIpAddressManagerFilter({
            ...this.getInternalIpAddressManagerFilter,
            subnetAddress: [{ subnetId }],
            subnetIpAddressOrDescription: ipAddress
          })

          this.$router.push({
            name: routeName
          })
          break
        default:
          this.$router.push({
            name: routeName,
            params: { id: resource.id }
          })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.icon-menu-container {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(115, 103, 240, 0.12);
  min-width: 38px;
  min-height: 38px;
  border-radius: 4px;
  aspect-ratio: 1/1;
}

.sub-header {
  font-weight: 400;
  font-size: 12px;
  color: #b9b9c3;
}

.header {
  display: block;
  font-weight: 600;
  font-size: 14px;
  color: #6e6b7b;

  overflow-wrap: break-word;
  word-break: break-word;
}

.header-link {
  text-decoration: underline;
  cursor: pointer;
}

.badge-style {
  min-width: 70px;
  min-height: 24px;
  font-size: 13px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.table-wrapper ::v-deep.b-table-sticky-header {
  // max-height: calc(100vh - 625px);
  &:has(.b-table-empty-row) {
    min-height: 100px;
  }
}
</style>
